/* eslint-disable @typescript-eslint/naming-convention */
import * as React from "react";
import { useCallback, useState } from "react";
import { Light as SyntaxHighlighter } from "react-syntax-highlighter";
import syntaxTheme from "../../../shared-components/syntaxTheme";
import syntaxHighlighterStyle from "../../../shared-components/syntaxHighlighterStyle";
import { Alert } from "../../../components/ui/alert";
import { CardContent } from "../../../components/ui/card";
import { BadgeWithClose } from "../../../components/system/badge-with-close";
import { Stack } from "../../../components/ui/stack";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "../../../components/ui/pagination";
import { useClusterAnalyticsSummary } from "../../../crud/analytics/hooks";
import { NumberParam, useQueryParam, withDefault } from "use-query-params";
import { ComputationStatistics } from "./ComputationsStatistics";
import { ComputationTimeline } from "./charts/ComputationTimeline";
import {
  StackCode,
  StackSpecificFrame,
  ComputationSummary,
  ExceptionSummary,
} from "../Analytics/ClusterStatistics";
import { ExceptionAlert } from "./ExceptionAlert";
import { ExceptionDialog } from "./ExceptionDialog";
import { timeRangeToString } from "./utils";
import { TimeRange } from "../types";

/*
A table listing the computations for a cluster.
*/
export const ComputationListing = ({
  clusterId,
  focusedComputation,
  setFocusedComputation,
  timeRange,
  setTimeRange,
  defaultTimeRange,
  setRefAreaLeft,
  setRefAreaRight,
  zoomOut,
}: {
  clusterId?: number;
  focusedComputation?: string;
  setFocusedComputation: (computation?: string) => void;
  timeRange?: TimeRange;
  setTimeRange: (timeRange?: TimeRange) => void;
  defaultTimeRange?: TimeRange;
  setRefAreaLeft: (refAreaLeft: number) => void;
  setRefAreaRight: (refAreaRight: number) => void;
  zoomOut: () => void;
}): React.ReactElement => {
  const [selectedException, setSelectedException] =
    React.useState<ExceptionSummary | null>(null);
  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [hoveredComputation, setHoveredComputation] = useState<
    string | undefined
  >();

  const doComputationHover = (c?: ComputationSummary) => {
    if (c) {
      setHoveredComputation(c.identifier);

      setRefAreaLeft(c.start ? Math.floor(c.start * 1000) : 0);
      setRefAreaRight(c.stop ? Math.ceil(c.stop * 1000) : 0);
    } else {
      setHoveredComputation(undefined);
      setRefAreaLeft(0);
      setRefAreaRight(0);
    }
  };

  const doComputationClick = (c?: ComputationSummary) => {
    if (c) {
      setFocusedComputation(c.identifier);
      if (c.start && c.stop) {
        setTimeRange([Math.floor(c.start * 1000), Math.ceil(c.stop * 1000)]);
      }
    } else {
      zoomOut();
    }
  };

  const rowsPerPage = 20;

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleModalClose = useCallback(() => {
    setSelectedException(null);
  }, [setSelectedException]);

  const { data, isLoading } = useClusterAnalyticsSummary(
    clusterId,
    defaultTimeRange,
  );

  const computations = data?.computations || [];

  // Deduplicate computations by identifier to prevent duplicates
  const uniqueComputations = React.useMemo(() => {
    const seen = new Set();
    return computations.filter((c) => {
      if (seen.has(c.identifier)) {
        return false;
      }
      seen.add(c.identifier);
      return true;
    });
  }, [computations]);

  const filteredComputations = uniqueComputations.filter((c) => {
    if (timeRange && c.start && c.stop) {
      const startTs = new Date(c.start * 1000).getTime();
      const stopTs = new Date(c.stop * 1000).getTime();

      // check if the computation start or stop overlaps with the selected time range
      if (isOverlapping([startTs, stopTs], timeRange)) {
        return true;
      } else {
        return false;
      }
    }

    return true;
  });
  const hasZoomed = timeRange !== defaultTimeRange;
  const focusedComputations = filteredComputations.filter(
    (c) => !focusedComputation || c.identifier === focusedComputation,
  );

  return (
    <div id="computation-listing">
      {computations.length === 0 && !isLoading ? (
        <Alert severity="info" className="m-2">
          No computations detected on this cluster. See{" "}
          <a href="https://docs.coiled.io/user_guide/examples/index.html">
            Run an example computation
          </a>{" "}
          to get started.
        </Alert>
      ) : null}
      {computations.length > 0 ? (
        <CardContent>
          <ComputationTimeline
            computations={filteredComputations}
            timeRange={timeRange}
            focusedComputation={focusedComputation}
            hoveredComputation={hoveredComputation}
            doComputationClick={doComputationClick}
            doComputationHover={doComputationHover}
          />
        </CardContent>
      ) : null}
      {timeRange && hasZoomed && (
        <div className="pb-4">
          <span>
            Showing {focusedComputation ? "1 computation" : "computations"}{" "}
            between
          </span>{" "}
          <BadgeWithClose variant="outline" onClose={zoomOut} className="ml-2">
            {timeRangeToString(timeRange)}
          </BadgeWithClose>
        </div>
      )}
      <Stack>
        {focusedComputations
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((c) => {
            return (
              <ComputationRow
                key={c.identifier}
                focused={focusedComputation === c.identifier}
                computation={c}
                setSelectedException={setSelectedException}
                hoveredComputation={hoveredComputation}
                doComputationHover={doComputationHover}
                doComputationFocus={doComputationClick}
              />
            );
          })}
      </Stack>
      {!focusedComputation ? (
        <CustomPagination
          count={focusedComputations.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
        />
      ) : null}
      <ExceptionDialog
        open={!!selectedException}
        exception={selectedException}
        handleClose={handleModalClose}
      />
    </div>
  );
};

// Custom pagination component using shadcn pagination components
const CustomPagination = ({
  count,
  rowsPerPage,
  page,
  onPageChange,
}: {
  count: number;
  rowsPerPage: number;
  page: number;
  onPageChange: (event: unknown, newPage: number) => void;
}) => {
  const totalPages = Math.ceil(count / rowsPerPage);
  const startItem = page * rowsPerPage + 1;
  const endItem = Math.min((page + 1) * rowsPerPage, count);

  if (totalPages <= 1) return null;

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    if (totalPages <= maxVisiblePages) {
      for (let i = 0; i < totalPages; i++) {
        pages.push(
          <PaginationItem key={i}>
            <PaginationLink
              isActive={page === i}
              onClick={() => onPageChange(null, i)}
            >
              {i + 1}
            </PaginationLink>
          </PaginationItem>,
        );
      }
    } else {
      // Show first page
      pages.push(
        <PaginationItem key={0}>
          <PaginationLink
            isActive={page === 0}
            onClick={() => onPageChange(null, 0)}
          >
            1
          </PaginationLink>
        </PaginationItem>,
      );

      // Show ellipsis if needed
      if (page > 2) {
        pages.push(
          <PaginationItem key="ellipsis1">
            <PaginationEllipsis />
          </PaginationItem>,
        );
      }

      // Show pages around current page
      const start = Math.max(1, page - 1);
      const end = Math.min(totalPages - 2, page + 1);
      for (let i = start; i <= end; i++) {
        pages.push(
          <PaginationItem key={i}>
            <PaginationLink
              isActive={page === i}
              onClick={() => onPageChange(null, i)}
            >
              {i + 1}
            </PaginationLink>
          </PaginationItem>,
        );
      }

      // Show ellipsis if needed
      if (page < totalPages - 3) {
        pages.push(
          <PaginationItem key="ellipsis2">
            <PaginationEllipsis />
          </PaginationItem>,
        );
      }

      // Show last page
      if (totalPages > 1) {
        pages.push(
          <PaginationItem key={totalPages - 1}>
            <PaginationLink
              isActive={page === totalPages - 1}
              onClick={() => onPageChange(null, totalPages - 1)}
            >
              {totalPages}
            </PaginationLink>
          </PaginationItem>,
        );
      }
    }

    return pages;
  };

  return (
    <div className="flex items-center justify-between px-2 py-4">
      <div className="flex-1 text-sm text-muted-foreground">
        {count > 0 ? (
          <>
            {startItem}-{endItem} of {count}
          </>
        ) : (
          "0 of 0"
        )}
      </div>
      <Pagination>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={() => page > 0 && onPageChange(null, page - 1)}
              className={page === 0 ? "pointer-events-none opacity-50" : ""}
            />
          </PaginationItem>
          {renderPageNumbers()}
          <PaginationItem>
            <PaginationNext
              onClick={() =>
                page < totalPages - 1 && onPageChange(null, page + 1)
              }
              className={
                page >= totalPages - 1 ? "pointer-events-none opacity-50" : ""
              }
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};

const isOverlapping = (timeRange1: TimeRange, timeRange2: TimeRange) =>
  Math.max(timeRange1[0] as number, timeRange2[0] as number) <=
  Math.min(timeRange1[1] as number, timeRange2[1] as number);

const getLanguageFromFile = (filename?: string, code?: string): string => {
  if (filename) {
    if (filename.endsWith(".sh")) {
      return "bash";
    } else if (filename.endsWith(".py")) {
      return "python";
    }
  }

  // Try to detect from content
  if (code) {
    const firstLine = code.split("\n")[0];
    if (firstLine.startsWith("#!") && firstLine.endsWith("sh")) {
      return "bash";
    }
  }

  return "python"; // Default to python
};

const CodeFrameCode = (props: {
  code: StackCode;
  codeIdx: number;
  label: string | undefined;
}) => {
  const highlightLines = props.code.frames.map(
    (frame: StackSpecificFrame) => frame.relativeLine,
  );
  const showSpanName = props.label && props.label !== "default";
  return (
    <div className="break-words">
      <b className="break-words">
        {showSpanName ? (
          <>
            Span <u>{props.label}</u>
          </>
        ) : props.codeIdx ? (
          <br />
        ) : (
          ""
        )}
        {props.code.filename &&
        !props.code.filename.includes("ipython") &&
        !props.code.filename.includes("ipykernel")
          ? showSpanName
            ? ` in ${props.code.filename}`
            : props.code.filename
          : ""}
      </b>
      <SyntaxHighlighter
        language={getLanguageFromFile(props.code.filename, props.code.code)}
        style={syntaxTheme}
        customStyle={{
          ...syntaxHighlighterStyle,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          overflowWrap: "break-word",
        }}
        lineNumberStyle={{ display: "none" }}
        showLineNumbers
        wrapLines
        lineProps={(lineNumber: number) => {
          // subtract 1 since line numbers start at 1 rather than 0
          const style = highlightLines.includes(lineNumber - 1)
            ? {
                display: "block",
                fontWeight: "bold",
                backgroundColor:
                  highlightLines[highlightLines.length - 1] === lineNumber - 1
                    ? "#ddddff"
                    : "#ddd",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                textShadow: "none",
              }
            : {
                display: "block",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                textShadow: "none",
              };
          return { style };
        }}
      >
        {props.code.code}
      </SyntaxHighlighter>
    </div>
  );
};

/*
A single table row that can expand to show more information about a computation.
*/
const ComputationRow = (props: {
  computation: ComputationSummary;
  focused: boolean;
  setSelectedException: (exception: ExceptionSummary | null) => void;
  hoveredComputation: string | undefined;
  doComputationHover: (c?: ComputationSummary) => void;
  doComputationFocus: (c: ComputationSummary) => void;
}) => {
  const { computation, hoveredComputation, doComputationHover } = props;

  const isHovered = hoveredComputation === computation.identifier;

  return (
    <Stack
      direction="row"
      alignItems="start"
      justifyContent="between"
      className="w-full"
    >
      <Stack direction="column" alignItems="start" className="flex-1 min-w-0">
        <div
          onClick={() => props.doComputationFocus(computation)}
          onMouseEnter={() => doComputationHover(computation)}
          onMouseLeave={() => doComputationHover(undefined)}
          className={`text-sm p-2 rounded cursor-pointer border transition-colors ${
            props.focused || isHovered
              ? "border-blue-300 bg-blue-50"
              : "border-transparent bg-blue-50"
          } hover:border-blue-300 hover:bg-blue-100 w-full`}
        >
          <div className="flex flex-wrap gap-2">
            {computation.callstackDeduped.map((frame, index) => (
              <div key={index} className="flex-shrink-0 max-w-full">
                <CodeFrameCode
                  label={computation.label}
                  code={frame}
                  codeIdx={index}
                />
              </div>
            ))}
          </div>
        </div>
        <div className="pl-4">
          {computation.exceptions.map((exception) => (
            <ExceptionAlert
              key={exception.exception}
              exception={exception}
              setSelectedException={props.setSelectedException}
            />
          ))}
        </div>
      </Stack>
      <div className="w-80 flex-shrink-0">
        <ComputationStatistics computation={computation} />
      </div>
    </Stack>
  );
};
